import { useMemo } from 'react';
import { ContextKindKeyBadge } from '@gonfalon/contexts/components/ContextKindKeyBadge';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { getExperimentKindLabel } from '@gonfalon/experiments/getExperimentKindLabel';
import {
  EXPERIMENT_APPROVAL_INSTRUCTION_MODE,
  type ExperimentApprovalInstructionMode,
} from '@gonfalon/experiments/instructions';
import { roundAllocationValue } from '@gonfalon/experiments/roundAllocationValue';
import { DEFAULT_CONTEXT_KIND, ExperimentStatusKind } from '@gonfalon/experiments/types';
import type { Experiment, Iteration, Treatment } from '@gonfalon/experiments/types';
import { VariationDisplay } from '@gonfalon/flag-variations/VariationDisplay';
import type { FeatureFlag, FeatureFlagConfig, Variation } from '@gonfalon/flags/types';
import { Diff, stringifyForDiff } from '@gonfalon/launchpad-experimental/Diff';
import { toHoldoutDetails } from '@gonfalon/navigator/navigators';
import { useHoldoutById } from '@gonfalon/rest-api/hooks/useHoldoutById';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { capitalize } from '@gonfalon/strings/capitalize';
import { Details } from '@gonfalon/ui/details';
import { Link, Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { ExperimentStatusIcon } from '#components/experimentation/common/components/ExperimentStatusIcon';
import {
  type ApprovalInstructionReason,
  type ApprovalInstructionsInput,
  getExperimentApprovalDisplayState,
  getShippingTreatments,
} from '#components/experimentation/common/utils/approvalInstructionTreatments';
import { singleFlagKey } from '#components/experimentation/common/utils/flag';
import { EXPERIMENT_STATUS_TAG_MAP } from '#components/experimentation/experiments/dashboard/ExperimentsList/utils/experimentStatusTagMap';
import { OnOffChip } from '#components/OnOffChip';
import { useReviewAndSaveContext } from '#components/PendingChanges/NewPendingChangesForm/reviewAndSaveContext';
import { isAiConfigFlag } from '#utils/flags/flagPurpose';
import { translateFlagConfigToJson } from '#utils/flags/json/utils';

import styles from './ExperimentApprovalChangesCard.module.css';

export type ExperimentApprovalChangesProps = {
  experiment: Experiment;
  flagData: FeatureFlag;
  environmentKey: string;
  mode?: ExperimentApprovalInstructionMode;
  instructions?: ApprovalInstructionsInput;
};

/**
 * Renders the experiment approval changes content (visual or JSON diff).
 * Must be used within a ReviewAndSaveContextProvider.
 * Use ExperimentApprovalChangesCard for a self-contained version with header and toolbar.
 */
export function ExperimentApprovalChanges({
  experiment,
  flagData,
  environmentKey,
  mode = EXPERIMENT_APPROVAL_INSTRUCTION_MODE.START,
  instructions,
}: ExperimentApprovalChangesProps) {
  const { isJsonDiffingEnabled, expandAll, collapseState, setCollapseState } = useReviewAndSaveContext();

  const {
    mode: resolvedMode,
    experiment: experimentForDisplay,
    approvalReason,
  } = getExperimentApprovalDisplayState({
    experiment,
    instructions,
    fallbackMode: mode,
  });

  const typeLabel = getExperimentKindLabel(experimentForDisplay.type ?? 'experiment');
  const experimentKey = experimentForDisplay.key;
  const previousIterations = experimentForDisplay?.previousIterations ?? [];
  const currentIteration = experimentForDisplay.currentIteration;
  const draftIteration = experimentForDisplay.draftIteration;
  const activeIteration = draftIteration ?? currentIteration;
  const flagKey = singleFlagKey(activeIteration) ?? '';
  const variations = flagData.variations;
  const flagEnvConfig = flagData.environments?.[environmentKey];
  const isFlagOff = flagEnvConfig && !flagEnvConfig.on;

  const targetingRuleId = activeIteration?.flags?.[flagKey]?.targetingRule;
  const targetRuleName = getTargetRuleName(flagEnvConfig, targetingRuleId);

  const treatments = activeIteration?.treatments ?? [];
  const randomizationUnit = activeIteration?.randomizationUnit ?? DEFAULT_CONTEXT_KIND;
  const nextStatus =
    resolvedMode === EXPERIMENT_APPROVAL_INSTRUCTION_MODE.STOP
      ? ExperimentStatusKind.STOPPED
      : ExperimentStatusKind.RUNNING;

  if (isJsonDiffingEnabled) {
    return (
      <JsonChanges
        flagKey={flagKey}
        flagData={flagData}
        environmentKey={environmentKey}
        currentIteration={currentIteration}
        draftIteration={draftIteration}
        nextStatus={nextStatus}
        typeLabel={typeLabel}
        isAiConfig={isAiConfigFlag(flagData._purpose)}
      />
    );
  }

  return (
    <VisualChanges
      experimentKey={experimentKey}
      typeLabel={typeLabel}
      isFlagOff={isFlagOff}
      treatments={treatments}
      randomizationUnit={randomizationUnit}
      variations={variations}
      targetRuleName={targetRuleName}
      expandAll={expandAll}
      collapseState={collapseState}
      setCollapseState={setCollapseState}
      nextStatus={nextStatus}
      previousIterations={previousIterations}
      mode={resolvedMode}
      approvalReason={approvalReason}
      holdoutId={experiment.holdoutId}
      environmentKey={environmentKey}
      isAiConfig={isAiConfigFlag(flagData._purpose)}
    />
  );
}

function VisualChanges({
  experimentKey,
  typeLabel,
  isFlagOff,
  treatments,
  randomizationUnit,
  variations,
  targetRuleName,
  expandAll,
  collapseState,
  setCollapseState,
  nextStatus,
  mode,
  previousIterations,
  approvalReason,
  holdoutId,
  environmentKey,
  isAiConfig,
}: {
  experimentKey: string;
  typeLabel: string;
  previousIterations: Iteration[];
  isFlagOff?: boolean;
  treatments: Treatment[];
  randomizationUnit: string;
  variations: Variation[];
  targetRuleName?: string;
  expandAll: boolean | undefined;
  collapseState: Record<string, boolean | undefined>;
  setCollapseState: (id: string, open: boolean) => void;
  nextStatus: ExperimentStatusKind;
  mode: ExperimentApprovalInstructionMode;
  approvalReason?: ApprovalInstructionReason;
  holdoutId?: string;
  environmentKey: string;
  isAiConfig: boolean;
}) {
  const projectKey = useProjectKey();
  const { data: holdout } = useHoldoutById(
    { projectKey, environmentKey, holdoutId: holdoutId ?? '' },
    { enabled: !!holdoutId },
  );

  const hasFlagChanges = isFlagOff || (targetRuleName && treatments.length > 0);
  const getIsOpen = (id: string) => collapseState[id] ?? expandAll ?? true;
  const handleOpenChange = (id: string) => (open: boolean) => setCollapseState(id, open);

  const showsNewIterationTransition = mode === EXPERIMENT_APPROVAL_INSTRUCTION_MODE.SAVE_AND_START;

  const iterationNumber = previousIterations.length + 1;

  const holdoutName = holdout?.baseExperiment?.name ?? holdoutId;
  const holdoutKey = holdout?.baseExperiment?.key ?? '';
  const holdoutAmount = holdout?.holdoutAmount;
  const holdoutLink = holdoutId ? toHoldoutDetails({ projectKey, environmentKey, holdoutKey }) : undefined;

  return (
    <div className={styles.visualChanges}>
      <div className={styles.instructionCard}>
        <Details
          open={getIsOpen('experiment')}
          onOpenChange={handleOpenChange('experiment')}
          summary={capitalize(typeLabel)}
          prependCaret
        >
          <div className={styles.instructionList}>
            {showsNewIterationTransition && (
              <div className={styles.instructionItem}>
                <Icon name="change-history" size="small" className={styles.changeHistoryIcon} />
                <span className={styles.instructionText}>
                  Update {typeLabel} to iteration {iterationNumber}{' '}
                  <TagGroup aria-label="experiment status">
                    <TagList>
                      <Tag
                        size="medium"
                        variant={EXPERIMENT_STATUS_TAG_MAP[ExperimentStatusKind.STOPPED]}
                        textValue={ExperimentStatusKind.STOPPED}
                        className={styles.statusTag}
                      >
                        <ExperimentStatusIcon status={ExperimentStatusKind.STOPPED} kind="text-only" />
                      </Tag>
                    </TagList>
                  </TagGroup>
                </span>
              </div>
            )}
            <div className={styles.instructionItem}>
              <Icon name="change-history" size="small" className={styles.changeHistoryIcon} />
              <span className={styles.instructionText}>
                Update {typeLabel} to {showsNewIterationTransition && `iteration ${iterationNumber + 1}`}{' '}
                <TagGroup aria-label="experiment status">
                  <TagList>
                    <Tag
                      size="medium"
                      variant={EXPERIMENT_STATUS_TAG_MAP[nextStatus]}
                      textValue={nextStatus}
                      className={styles.statusTag}
                    >
                      <ExperimentStatusIcon status={nextStatus} kind="text-only" />
                    </Tag>
                  </TagList>
                </TagGroup>
              </span>
            </div>
            {holdout && holdoutId && holdoutAmount !== undefined && (
              <div className={styles.instructionItem}>
                <Icon name="change-history" size="small" className={styles.changeHistoryIcon} />
                <span className={styles.instructionText}>
                  Added to a {holdoutAmount}% holdout,{' '}
                  <Link href={holdoutLink}>
                    <code className={styles.holdoutBadge}>{holdoutName}</code>
                  </Link>
                </span>
              </div>
            )}
          </div>
        </Details>
      </div>

      {approvalReason && (
        <div className={styles.stopReasonContainer}>
          <span className={styles.stopReasonLabel}>{approvalReason.label}</span>
          <p className={styles.stopReasonText}>{approvalReason.text}</p>
        </div>
      )}

      {hasFlagChanges && (
        <div className={styles.flagChangesGroup}>
          <p className={styles.connectorText}>
            Due to updates to the <strong>{experimentKey}</strong> {typeLabel}:
          </p>

          <div className={styles.instructionCard}>
            {isFlagOff && (
              <Details
                open={getIsOpen('flag-targeting')}
                onOpenChange={handleOpenChange('flag-targeting')}
                summary={isAiConfig ? 'Config targeting' : 'Flag targeting'}
                prependCaret
                className={styles.cardSection}
              >
                <div className={styles.instructionList}>
                  <div className={styles.instructionItem}>
                    <Icon name="change-history" size="small" className={styles.changeHistoryIcon} />
                    <span className={styles.instructionText}>
                      Update targeting to <OnOffChip isOn />
                    </span>
                  </div>
                </div>
              </Details>
            )}

            {targetRuleName && treatments.length > 0 && (
              <Details
                open={getIsOpen('flag-targeting-rules')}
                onOpenChange={handleOpenChange('flag-targeting-rules')}
                summary={isAiConfig ? 'Config targeting rules' : 'Flag targeting rules'}
                prependCaret
              >
                <div className={styles.instructionList}>
                  <div className={styles.instructionItem}>
                    <Icon name="change-history" size="small" className={styles.changeHistoryIcon} />
                    <span>
                      Update targeting rule, <strong>{targetRuleName}</strong>, variation split to
                    </span>
                  </div>
                  <div className={styles.variationList}>
                    {getShippingTreatments(treatments).map((treatment) => {
                      const variation = variations.find((v) => v._id === treatment.parameters?.[0]?.variationId);
                      const variationIndex = variation ? variations.indexOf(variation) : 0;
                      return (
                        <div
                          key={treatment._id ?? treatment.parameters?.[0]?.variationId}
                          className={styles.variationEntry}
                        >
                          {variation && <VariationDisplay variation={variation} variationIndex={variationIndex} />}
                          <span>({roundAllocationValue(parseFloat(treatment.allocationPercent))}%)</span>
                        </div>
                      );
                    })}
                    <div className={styles.randomizedBy}>
                      randomized by <ContextKindKeyBadge contextKind={{ key: randomizationUnit }} />
                    </div>
                    {holdout && holdoutId && holdoutAmount !== undefined && (
                      <div className={styles.holdoutAllocationNote}>
                        Absolute value of allocation reduced by {holdoutAmount}% due to holdout,{' '}
                        <Link href={holdoutLink}>
                          <code className={styles.holdoutBadge}>{holdoutName}</code>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </Details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function JsonChanges({
  flagKey,
  flagData,
  environmentKey,
  currentIteration,
  draftIteration,
  nextStatus,
  typeLabel,
  isAiConfig,
}: {
  flagKey: string;
  flagData: FeatureFlag;
  environmentKey: string;
  currentIteration?: Iteration;
  draftIteration?: Iteration;
  nextStatus: ExperimentStatusKind;
  typeLabel: string;
  isAiConfig: boolean;
}) {
  const variations = flagData.variations;
  const flagEnvConfig = flagData.environments?.[environmentKey];
  const activeIteration = draftIteration ?? currentIteration;

  const originalExperimentJson = useMemo(() => {
    if (!currentIteration) {
      return stringifyForDiff({});
    }
    return stringifyForDiff(pickIterationFieldsForDiff(currentIteration));
  }, [currentIteration]);

  const compareExperimentJson = useMemo(() => {
    if (!activeIteration) {
      return stringifyForDiff({});
    }
    return stringifyForDiff(pickIterationFieldsForDiff({ ...activeIteration, status: nextStatus }));
  }, [activeIteration, nextStatus]);

  const originalFlagJson = useMemo(() => {
    if (!flagEnvConfig) {
      return stringifyForDiff({});
    }
    return stringifyForDiff(
      translateFlagConfigToJson({
        draftConfig: flagEnvConfig,
        originalConfig: flagEnvConfig,
        pendingGuardedRollout: undefined,
        variations,
        pendingProgressiveRollout: undefined,
      }),
    );
  }, [flagEnvConfig, variations]);

  const compareFlagJson = useMemo(() => {
    if (!flagEnvConfig || !activeIteration) {
      return stringifyForDiff({});
    }
    const draftConfig = buildFlagConfigAfterExperiment({
      flagEnvConfig,
      iteration: activeIteration,
      flagKey,
      variations,
    });
    return stringifyForDiff(
      translateFlagConfigToJson({
        draftConfig,
        originalConfig: flagEnvConfig,
        pendingGuardedRollout: undefined,
        variations,
        pendingProgressiveRollout: undefined,
      }),
    );
  }, [flagEnvConfig, activeIteration, flagKey, variations]);

  return (
    <ErrorBoundary severity="high" fallbackRender={() => <div>Error rendering JSON diff</div>}>
      <div className={styles.jsonChanges}>
        <div className={styles.jsonDiffSection}>
          <p className={styles.jsonDiffSectionTitle}>{capitalize(typeLabel)}</p>
          <Diff
            original={originalExperimentJson}
            compare={compareExperimentJson}
            includeSubDiffs
            isJSON
            showLineNumbers={false}
          />
        </div>

        {flagEnvConfig && (
          <div className={styles.jsonDiffSection}>
            <p className={styles.jsonDiffSectionTitle}>{isAiConfig ? 'Config targeting' : 'Flag configuration'}</p>
            <Diff
              original={originalFlagJson}
              compare={compareFlagJson}
              includeSubDiffs
              isJSON
              showLineNumbers={false}
            />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

function buildFlagConfigAfterExperiment({
  flagEnvConfig,
  iteration,
  flagKey,
  variations,
}: {
  flagEnvConfig: FeatureFlagConfig;
  iteration: Iteration;
  flagKey: string;
  variations: Variation[];
}): FeatureFlagConfig {
  const treatments = getShippingTreatments(iteration.treatments);
  const targetingRule = iteration.flags?.[flagKey]?.targetingRule;
  const randomizationUnit = iteration.randomizationUnit ?? DEFAULT_CONTEXT_KIND;

  const rolloutVariations = treatments.map((t) => {
    const variationId = t.parameters?.[0]?.variationId;
    const variationIndex = variations.findIndex((v) => v._id === variationId);
    return {
      variation: variationIndex >= 0 ? variationIndex : 0,
      weight: Math.round(parseFloat(t.allocationPercent) * 1000),
    };
  });

  const baselineTreatment = treatments.find((t) => t.baseline);
  const baselineVariationId = baselineTreatment?.parameters?.[0]?.variationId;
  const defaultVariation = baselineVariationId
    ? Math.max(
        0,
        variations.findIndex((v) => v._id === baselineVariationId),
      )
    : 0;

  const experimentRollout = {
    contextKind: randomizationUnit,
    experimentAllocation: {
      canReshuffle: iteration.canReshuffleTraffic ?? false,
      defaultVariation,
    },
    variations: rolloutVariations,
  };

  const rules = flagEnvConfig.rules?.map((r) => ({ ...r }));
  let fallthrough = flagEnvConfig.fallthrough;

  if (targetingRule === 'fallthrough') {
    fallthrough = { rollout: experimentRollout };
  } else if (targetingRule && rules) {
    const ruleIndex = rules.findIndex((r) => r._id === targetingRule);
    if (ruleIndex >= 0) {
      const { variation: _v, ...ruleFields } = rules[ruleIndex];
      rules[ruleIndex] = { ...ruleFields, rollout: experimentRollout };
    }
  }

  return {
    ...flagEnvConfig,
    on: true,
    rules,
    fallthrough,
  };
}

function pickIterationFieldsForDiff(iteration: Iteration) {
  return {
    status: iteration.status,
    hypothesis: iteration.hypothesis,
    randomizationUnit: iteration.randomizationUnit,
    treatments: iteration.treatments
      ? getShippingTreatments(iteration.treatments).map((t) => ({
          name: t.name,
          allocationPercent: t.allocationPercent,
        }))
      : undefined,
    flags: iteration.flags
      ? Object.fromEntries(
          Object.entries(iteration.flags).map(([key, flag]) => [key, { targetingRule: flag?.targetingRule }]),
        )
      : undefined,
    metrics: iteration.metrics?.map((m) => ({
      key: m.key,
      name: m.name,
      isGroup: m.isGroup,
    })),
  };
}

function getTargetRuleName(flagEnvConfig: FeatureFlagConfig | undefined, targetingRuleId: string | undefined) {
  if (targetingRuleId === 'fallthrough') {
    return 'Default rule';
  }

  const rules = flagEnvConfig?.rules ?? [];
  const targetRule = rules.find((rule) => rule._id === targetingRuleId);
  const targetRuleIndex = targetRule ? rules.indexOf(targetRule) : -1;

  return targetRule?.description ?? (targetRuleIndex >= 0 ? `Rule ${targetRuleIndex + 1}` : undefined);
}
